import { useContext, useEffect, useMemo } from "react";
import { createFileRoute, useSearch, useLocation } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { clientDeploymentsQueryOptions, clientDeploymentsByBrandingQueryOptions } from "@/server/client-management";
import { ClientManagementTable } from "./-components/client-management-table";
import { DashboardContext, DashboardLayout } from "@/components/dashboard-layout";
import { ClientDetails, ClientDetailsSkeleton } from "./-components/client-management-sidebar";
import { type SubmenuItem } from "@/components/submenu";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

export const Route = createFileRoute("/_dashboard/client-management")({
  component: ClientManagementRouteComponent,
  beforeLoad: () => {
    return { title: "Client Management" };
  },
  loader: () => {
    return {
      crumb: "Client management",
    };
  },
});

function ClientManagementRouteComponent() {
  const clientDeploymentsQuery = useQuery(clientDeploymentsQueryOptions);

  useEffect(() => {
    if (clientDeploymentsQuery.isError) {
      const message =
        clientDeploymentsQuery.error instanceof Error
          ? clientDeploymentsQuery.error.message
          : "Failed to load client deployments. Please try again later.";
      toast.error(message);
    }
  }, [clientDeploymentsQuery.isError, clientDeploymentsQuery.error]);

  const submenuConfig = useMemo(() => {
    if (!clientDeploymentsQuery.data || !clientDeploymentsQuery.data.brandings) {
      return null;
    }

    // If one or zero brandings, don't show submenu
    if (clientDeploymentsQuery.data.brandings.length <= 1) {
      return null;
    }

    if (clientDeploymentsQuery.isLoading) {
      return {
        items: [],
        title: "Loading Brandings...",
        // Don't pass queryOptions when we're handling data transformation locally
      };
    }

    const brandingItems: SubmenuItem[] = clientDeploymentsQuery.data.brandings.map((branding) => ({
      id: branding,
      name: branding,
      href: `/client-management?branding=${branding}`,
    }));

    return {
      items: brandingItems,
      title: "Brandings",
      // Don't pass queryOptions when we're handling data transformation locally
    };
  }, [clientDeploymentsQuery.data, clientDeploymentsQuery.isLoading]);

  return (
    <DashboardLayout
      submenu={
        submenuConfig
          ? {
            ...submenuConfig,
            active: true,
            onClick: undefined,
          }
          : undefined
      }
    >
      <ClientManagementContent />
    </DashboardLayout>
  );
}

function ClientManagementContent() {
  const searchParams = useSearch({ strict: false }) as {
    id?: string;
    branding?: string;
  };
  const location = useLocation();
  const context = useContext(DashboardContext);

  const clientDeploymentsQuery = useQuery(clientDeploymentsQueryOptions);
  const brandingSpecificQuery = useQuery(
    searchParams.branding
      ? clientDeploymentsByBrandingQueryOptions(searchParams.branding)
      : { queryKey: [], queryFn: () => null, enabled: false }
  );

  const clientData = searchParams.branding ? brandingSpecificQuery.data : clientDeploymentsQuery.data;
  const isLoading = searchParams.branding ? brandingSpecificQuery.isLoading : clientDeploymentsQuery.isLoading;
  const clients = clientData?.clients || [];

  if (!context) {
    console.error("DashboardContext is null. Ensure DashboardLayout wraps this component.");
    return <div>Error: DashboardContext is not available.</div>;
  }

  const isExact = location.href === "/client-management" || location.href === "/client-management/";

  const selectedClient = useMemo(() => {
    if (!searchParams.id || !clients?.length) return null;
    const foundClient = clients.find((client) => client.id === Number(searchParams.id));
    return foundClient || null;
  }, [searchParams.id, clients]);

  const sidebarComponent = useMemo(() => {
    if (!searchParams.id) return null;
    if (isLoading) return <ClientDetailsSkeleton />;
    if (selectedClient) return <ClientDetails client={selectedClient} />;
    return null;
  }, [searchParams.id, isLoading, selectedClient]);

  useEffect(() => {
    if (!context?.setRightSidebar) {
      return;
    }

    if (isExact) {
      context.setRightSidebar(null);
      return;
    }

    if (searchParams.id) {
      context.setRightSidebar(sidebarComponent);
      context.setRightSidebarParamKey?.("id");
    } else {
      context.setRightSidebar(null);
    }
  }, [searchParams.id, isExact, context?.setRightSidebar, context?.setRightSidebarParamKey, sidebarComponent]);

  const filteredClientData = useMemo(() => {
    if (!clientData || !clientData.brandings || clientData.brandings.length <= 1) {
      return clientData;
    }

    if (!searchParams.branding) {
      return null;
    }

    return clientData;
  }, [clientData, searchParams.branding]);

  const showBrandingSelection =
    clientDeploymentsQuery.data &&
    clientDeploymentsQuery.data.brandings &&
    clientDeploymentsQuery.data.brandings.length > 1 &&
    !searchParams.branding;

  const showNoDataMessage = clientDeploymentsQuery.data?.brandings?.length === 0;

  const shouldShowTable =
    clientDeploymentsQuery.data?.brandings?.length === 1 || // Single branding case
    ((clientDeploymentsQuery.data?.brandings?.length || 0) > 1 && searchParams.branding); // Multiple brandings with selection

  if (isLoading) {
    return <ClientManagementTable clientData={undefined} isLoading={true} />;
  }

  if (showBrandingSelection) {
    return (
      <Card>
        <CardContent>
          <p className="text-md pb-20 pt-15 text-center">
            Please choose a branding from the sidebar to view its client data.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (showNoDataMessage) {
    return (
      <Card>
        <CardContent>
          <p className="text-md pb-20 pt-15 text-center">No client brandings are currently deployed to this server.</p>
        </CardContent>
      </Card>
    );
  }

  if (shouldShowTable) {
    return <ClientManagementTable clientData={filteredClientData} isLoading={isLoading} />;
  }
  return (
    <Card>
      <CardContent>
        <p className="text-md pb-20 pt-15 text-center">No client data available</p>
      </CardContent>
    </Card>
  );
}
